import React, { Component } from 'react';
import { ICPOInternalTransfersGridState } from '../../types/stateTypes';
import CPOInternalTransfersServices from '../../services/cpo-internal-transfers/cpo-internal-transfers-service';
import { ICPOInternalTransfersList, ICPOTransfersDetails } from '../../models/organisation-invoices';
import moment from 'moment';
import StandardResponse from '../../models/standard-response';
import { toast } from 'react-toastify';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import { SelectBox } from '../../components/reactstrap-formik';
import Table from '../../components/table/Table';
import CPOInterfaceService from '../../services/cpo-interface/cpo-interface-service';
import { AppState } from '../../store/state/app.state';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { locale, regionDateAndTime } from '../../utils';
import { ICPOInternalTransfersGridProps } from '../../types/propTypes';
import { ICPOInterfacesList } from '../../models/cpo-interface';
import { getCurrencyFormat, getFormattedCurrencyWithSymbol } from '../../components/common/LocalNumberFormat';

export class CPOInternalTransfersGrid extends Component<
    ICPOInternalTransfersGridProps & WithTranslation,
    ICPOInternalTransfersGridState
> {
    cpoInternalTransfersServices: CPOInternalTransfersServices;
    cpoInterfaceService: CPOInterfaceService;
    constructor(props: ICPOInternalTransfersGridProps & WithTranslation) {
        super(props);
        this.state = {
            transfersList: [],
            columns: [],
            currentPage: 1,
            offset: 0,
            rowsPerPage: 10,
            sortColumn: 'created_date',
            sortOrder: 'desc',
            totalCount: 0,
            selectOptions: {
                cposList: [],
            },
            searchForm: {
                cpo: null,
                start_date: null,
                end_date: null,
            },
            search: {
                cpo_id: null,
                start_date: null,
                end_date: null,
            },
            isDefaultLoad: true,
        };
        this.cpoInternalTransfersServices = new CPOInternalTransfersServices();
        this.cpoInterfaceService = new CPOInterfaceService();
    }

    async componentDidMount() {
        const { t } = this.props;
        const { selectOptions } = this.state;
        let cposList = await this.getCPOsList();

        const header = [
            {
                label: t('CPOInternalTransfers.label.transfer_id'),
                id: 'transfer_id',
                sortable: true,
                isVisible: true,
                cell: (row: ICPOTransfersDetails) => {
                    return (
                        <span>
                            <u>
                                <a
                                    href={`/admin/cpo/transfer-detail/${row.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="themeColor"
                                >
                                    {row.transfer_id}
                                </a>
                            </u>
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.created_date'),
                id: 'created_date',
                sortable: true,
                isVisible: true,
                style: { textAlign: 'center' },
                cell: (row: ICPOTransfersDetails) => {
                    return row.created_date
                        ? moment(row.created_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT)
                        : null;
                },
            },
            {
                label: t('CPOInternalTransfers.label.gross_value'),
                id: 'gross_value',
                sortable: true,
                isVisible: true,
                className: 'text-center',
                cell: (row: ICPOTransfersDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal">
                            {getFormattedCurrencyWithSymbol(
                                row.gross_value?.toString(),
                                'text',
                                getCurrencyFormat()?.FINANCIAL_CURRENCY_FORMAT,
                                row?.currency_details?.symbol,
                                false,
                                true,
                            )}
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.action'),
                id: 'action',
                sortable: false,
                isVisible: false,
                style: { width: '100px', textAlign: 'center' },
            },
        ];
        this.setState({
            columns: header,
            selectOptions: {
                ...selectOptions,
                cposList,
            },
        });
    }

    getCPOsList = async () => {
        let cposList: any = [];

        const filterInput = {
            order: [['name', 'asc']],
        };
        await this.cpoInterfaceService
            .getCPOsList(filterInput)
            .then(async (res: StandardResponse<ICPOInterfacesList>) => {
                if (res?.Data?.rows) {
                    cposList = !_.isEmpty(res.Data.rows) && res.Data.rows.filter((item) => item.is_external);
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        return cposList;
    };

    syncSearch = (): Promise<boolean> => {
        return new Promise((resolve) => {
            const { search, searchForm } = this.state;

            search.start_date = _.isDate(searchForm.start_date)
                ? moment(searchForm.start_date).format('YYYY-MM-DD')
                : null;
            search.end_date = _.isDate(searchForm.end_date) ? moment(searchForm.end_date).format('YYYY-MM-DD') : null;
            search.cpo_id = !_.isEmpty(searchForm.cpo) ? searchForm.cpo?.id.toString() : null;

            this.setState({ search }, async () => {
                resolve(true);
            });
        });
    };

    getCPOInternalTransfersList = async () => {
        const { search } = this.state;

        let transfersList: Array<ICPOTransfersDetails> = [];

        if (!search.cpo_id) {
            toast.error(this.props.t('CPOInternalTransfers.errors.cpo.required'));
            return [];
        }

        const filterInput = _.pickBy(search, (value) => {
            return !_.isEmpty(value);
        });
        Object.assign(filterInput, {
            limit: this.state.rowsPerPage,
            offset: this.state.offset,
            order: [[this.state.sortColumn, this.state.sortOrder]],
        });
        await this.cpoInternalTransfersServices
            .getCPOInternalTransfersList(filterInput)
            .then(async (res: StandardResponse<ICPOInternalTransfersList>) => {
                transfersList = res.Data.rows;

                this.setState({ totalCount: res.Data.count });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        this.setState({ isDefaultLoad: false });
        return transfersList;
    };

    handleChange = (event) => {
        const { searchForm } = this.state;
        this.setState({
            searchForm: {
                ...searchForm,
                [event.target.name]: event.target.value,
            },
        });
    };

    handleChangeDropdown = (name, option) => {
        const { searchForm } = this.state;
        this.setState({
            searchForm: {
                ...searchForm,
                [name]: option,
            },
        });
    };

    handleSearch = () => {
        this.setState(
            {
                offset: 0,
                currentPage: 1,
            },
            () => {
                this.syncSearch().then(async () => {
                    const transfersList = await this.getCPOInternalTransfersList();
                    this.setState({ ...this.state, transfersList });
                });
            },
        );
    };

    handleReset = async () => {
        const searchForm = {
            cpo: null,
            start_date: null,
            end_date: null,
        };
        this.setState({
            ...this.state,
            searchForm,
            offset: 0,
            currentPage: 1,
            isDefaultLoad: true,
        });
    };
    handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.setState(
            {
                currentPage: currentPage,
                rowsPerPage: rowsPerPage,
                offset: offset,
            },
            () => {
                this.syncSearch().then(async () => {
                    const transfersList = await this.getCPOInternalTransfersList();
                    this.setState({ ...this.state, transfersList });
                });
            },
        );
    };

    handleSorting = async (columnName: string) => {
        let sortOrder = this.state.sortOrder;
        if (this.state.sortColumn === columnName) {
            sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            sortOrder = 'asc';
        }

        this.setState(
            {
                sortOrder: sortOrder,
                sortColumn: columnName,
            },
            () => {
                this.syncSearch().then(async () => {
                    const transfersList = await this.getCPOInternalTransfersList();
                    this.setState({ ...this.state, transfersList });
                });
            },
        );
    };

    handlePageRefresh = () => {
        this.syncSearch().then(async () => {
            const transfersList = await this.getCPOInternalTransfersList();
            this.setState({ ...this.state, transfersList });
        });
    };

    render() {
        const { t } = this.props;
        const { searchForm, selectOptions } = this.state;

        return (
            <>
                <div className="title row align-items-content justify-content-between pb-3">
                    <div className="col-auto col-sm text-truncate pr-0">
                        <h1 className="text-truncate">{t('CPOInternalTransfers.header')}</h1>
                    </div>
                </div>
                <div className="card-outer">
                    <div className="pt-3 px-3">
                        <form
                            noValidate
                            id="transfersSearchForm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                this.handleSearch();
                            }}
                            autoComplete="off"
                        >
                            <div className="row">
                                <div className="form-group col-md-6 col-lg-4 col-xl-3 min-height-38">
                                    <SelectBox
                                        name="cpo"
                                        class="form-control"
                                        value={searchForm.cpo}
                                        isMulti={false}
                                        options={selectOptions.cposList}
                                        onChange={(e) => this.handleChangeDropdown('cpo', e)}
                                        getOptionLabel={(opt) => {
                                            return opt.active
                                                ? opt.name
                                                : `${opt.name} - ${t('CPOInternalTransfers.label.disable')}`;
                                        }}
                                        getOptionValue={(opt) => opt.id}
                                        isClearable={true}
                                        placeholder={t('CPOInternalTransfers.placeholder.cpo')}
                                    />
                                </div>
                                <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                    <DatePicker
                                        name="start_date"
                                        className="form-control"
                                        selected={
                                            searchForm.start_date !== null && searchForm.start_date !== ''
                                                ? searchForm.start_date
                                                : null
                                        }
                                        placeholderText={t('CPOInternalTransfers.placeholder.start_date')}
                                        value={
                                            searchForm.start_date !== '' && searchForm.start_date !== null
                                                ? moment(searchForm.start_date).format(
                                                      regionDateAndTime().GENERAL_DATE_FORMAT,
                                                  )
                                                : moment(null)
                                        }
                                        dateFormat={regionDateAndTime().GENERAL_DATE_FORMAT}
                                        maxDate={searchForm.end_date !== null ? searchForm.end_date : moment().toDate()}
                                        onChange={(e) => this.handleChangeDropdown('start_date', e)}
                                        isClearable={true}
                                        locale={locale()}
                                    />
                                </div>
                                <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                    <DatePicker
                                        name="end_date"
                                        className="form-control"
                                        id="example-datepicker"
                                        selected={
                                            searchForm.end_date !== null && searchForm.end_date !== ''
                                                ? searchForm.end_date
                                                : null
                                        }
                                        placeholderText={t('CPOInternalTransfers.placeholder.end_date')}
                                        value={
                                            searchForm.end_date !== '' && searchForm.end_date !== null
                                                ? moment(searchForm.end_date).format(
                                                      regionDateAndTime().GENERAL_DATE_FORMAT,
                                                  )
                                                : moment(null)
                                        }
                                        minDate={searchForm.start_date !== null ? searchForm.start_date : moment(null)}
                                        maxDate={moment().toDate()}
                                        dateFormat={regionDateAndTime().GENERAL_DATE_FORMAT}
                                        onChange={(e) => this.handleChangeDropdown('end_date', e)}
                                        isClearable={true}
                                        locale={locale()}
                                    />
                                </div>
                                <div className="form-group col-sm-12 col-md-6 col-lg-6">
                                    <button
                                        type="submit"
                                        className="btn btn-primary mr-2"
                                        title={t('CPOInternalTransfers.button.search')}
                                    >
                                        <span>{t('CPOInternalTransfers.button.search')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        title={t('CPOInternalTransfers.button.reset')}
                                        onClick={() => this.handleReset()}
                                    >
                                        <span>{t('CPOInternalTransfers.button.reset')}</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                    {!this.state.isDefaultLoad ? (
                        <Table
                            columns={this.state.columns}
                            data={this.state.transfersList}
                            rowsPerPage={this.state.rowsPerPage}
                            currentPage={this.state.currentPage}
                            totalCount={this.state.totalCount}
                            sortColumn={this.state.sortColumn}
                            sortOrder={this.state.sortOrder}
                            handlePageChange={this.handlePageChange}
                            handleSorting={this.handleSorting}
                            t={this.props.t}
                            isCheckboxGrid={false}
                        />
                    ) : (
                        ''
                    )}
                </div>
            </>
        );
    }
}
const mapStateToProps = (state: AppState) => ({
    userData: state.UserData,
});
export default connect(mapStateToProps, null)(withTranslation()(CPOInternalTransfersGrid));
